import logoImage from "@/assets/images/logo.png"
import logoImageDark from "@/assets/images/logo-dark.png"
import { LoginForm } from "@/components/auth/login-form"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { useEffect, useState, useCallback } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CheckCircle } from "lucide-react"
import InvitationService from "@/services/invitation-service"
import { publicApi } from "@/lib/api/public"
import { getReturnUrl } from "@/utils/return-url"
import { withInvitationId } from "@/utils/invitation-url"

/**
 * Signin page component
 * Provides login form and social authentication options
 * Redirects to workspace after successful authentication, especially for invitation flows
 */
export function SigninPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [invitationMessage, setInvitationMessage] = useState<string | null>(null)
  const [prefilledEmail, setPrefilledEmail] = useState<string>("")
  const [isFromInvitation, setIsFromInvitation] = useState<boolean>(false)
  const [invitationId, setInvitationId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch invitation details when invitation ID is available
  const fetchInvitationDetails = useCallback(async (invitationIdParam: string) => {
    try {
      setIsLoading(true)
      setError(null)
      
      const details = await InvitationService.getInvitationDetails(invitationIdParam)
      setPrefilledEmail(details.email) // Set prefilled email from invitation details
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load invitation details'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Check if coming from invitation acceptance
  useEffect(() => {
    // Check URL parameters first
    const invitationIdParam = searchParams.get('invitationId')
    if (invitationIdParam) {
      setInvitationId(invitationIdParam)
      setIsFromInvitation(true)
      // Fetch invitation details to get the email
      fetchInvitationDetails(invitationIdParam)
    }

    // Then check location state
    const state = location.state as any
    if (state?.fromInvitation) {
      setIsFromInvitation(true)
      if (state?.invitationId) {
        setInvitationId(state.invitationId)
        // Fetch invitation details to get the email
        fetchInvitationDetails(state.invitationId)
      }
      if (state?.message) {
        setInvitationMessage(state.message)
      }
    }
  }, [location.state, searchParams, fetchInvitationDetails])

  const handleLoginSuccess = async () => {
    // If coming from invitation, redirect to app with workspace switch parameters
    // Invitation was already accepted in the invitation page
    if (isFromInvitation && invitationId) {
      navigate(withInvitationId('/app', invitationId), { replace: true })
      return
    }

    // Return the user to where they were headed. `redirectUrl` is what
    // SessionProvider / api-client / AgentAccessRequired append when they bounce
    // an unauthenticated request here; `returnUrl` is the custom-domain spelling.
    // Both are accepted so no caller's return path is silently dropped.
    const returnUrl = getReturnUrl(searchParams)
    if (returnUrl) {
      navigate(returnUrl, { replace: true })
      return
    }

    // Check if on custom domain - should redirect to agent chat, not app pages
    const hostname = window.location.hostname
    const isCustomDomain = !['localhost', '127.0.0.1'].includes(hostname) &&
                           !hostname.includes('taqat.ai')

    if (isCustomDomain) {
      // Lookup domain and redirect to agent chat
      try {
        const result = await publicApi.lookupDomain(hostname)
        if (result.found && result.agentId) {
          navigate(`/agent/${result.agentId}/chat/new`, { replace: true })
          return
        }
      } catch (error) {
        console.error('Domain lookup failed:', error)
      }
    }

    // Default: normal app redirect (only for non-custom domains)
    navigate("/app", { replace: true })
  }

  // Show loading state while fetching invitation details
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background decoration - same as home page */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
          <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 lg:w-96 lg:h-96 bg-primary-50 dark:bg-primary-950/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            <div className="bg-card shadow-xl rounded-2xl border border-border p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading invitation details...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decoration - same as home page */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute -bottom-8 left-20 w-72 h-72 lg:w-96 lg:h-96 bg-primary-50 dark:bg-primary-950/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        {/* Login Card - just the card, nothing else */}
        <div className="w-full max-w-md">
          <div className="bg-card shadow-xl rounded-2xl border border-border p-8">
            {/* Logo inside the card */}
            <div className="flex justify-center mb-6">
              <img
                src={logoImage}
                alt="Taqat AI Logo"
                className="h-10 w-auto block dark:hidden"
              />
              <img
                src={logoImageDark}
                alt="Taqat AI Logo"
                className="h-10 w-auto hidden dark:block"
              />
            </div>

            {/* Show error if invitation details failed to load */}
            {error && (
              <Alert className="border-red-200 bg-red-50 mb-6">
                <AlertDescription className="text-red-700">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Show invitation success message */}
            {invitationMessage && (
              <Alert className="border-green-200 bg-green-50 mb-6">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  {invitationMessage}
                  {isFromInvitation && (
                    <p className="text-sm text-green-600 mt-2">
                      Sign in with your existing account to access the workspace.
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <LoginForm
              onSuccess={handleLoginSuccess}
              prefilledEmail={prefilledEmail}
            />

            {/* Divider - Hidden until social login is implemented */}
            {/* <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-neutral-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-neutral-500">
                  Or continue with
                </span>
              </div>
            </div> */}

            {/* Social Login - Hidden until OAuth is implemented */}
            {/* <SocialLogin /> */}
          </div>
        </div>
      </div>
    </div>
  )
}
